import { MediaPlayer, MediaProvider, useMediaState } from "@vidstack/react";
import {
  defaultLayoutIcons,
  DefaultVideoLayout,
} from "@vidstack/react/player/layouts/default";
import "@vidstack/react/player/styles/default/theme.css";
import "@vidstack/react/player/styles/default/layouts/video.css";
import { Box, CircularProgress } from "@mui/material";
import { useRef } from "react";
import type { MediaPlayerInstance } from "@vidstack/react";

interface VideoPlayerProps {
  src: string;
  title?: string;
  initialTime?: number;
  onProgress?: (currentTime: number, duration: number) => void;
  onReachedEnd?: () => void;
  isMandatory?: boolean;
  // Already satisfied the mandatory first-watch requirement in a prior
  // session (e.g. assignment already completed) — skip the lock entirely.
  mandatoryAlreadySatisfied?: boolean;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  title,
  initialTime = 0,
  onProgress,
  onReachedEnd,
  isMandatory = true,
  mandatoryAlreadySatisfied = false,
}) => {
  const playerRef = useRef<MediaPlayerInstance>(null);
  const buffering = useMediaState("waiting", playerRef);
  const lastReportedRef = useRef(0);
  const maxWatchedTimeRef = useRef(initialTime);
  const lastTimeUpdateRef = useRef(initialTime);
  const unlockedRef = useRef(mandatoryAlreadySatisfied);

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        width: "100%",
      }}
    >
      <MediaPlayer
        ref={playerRef}
        src={src}
        title={title}
        playsInline
        onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
        onCanPlay={() => {
          if (initialTime > 0 && playerRef.current) {
            playerRef.current.currentTime = initialTime;
          }
        }}
        onTimeUpdate={({ currentTime }) => {
          if (!unlockedRef.current) {
            // Only trust this tick as genuine "watched" progress if it's a
            // small step since the last one — a seek jump (forward OR
            // backward) reports a large delta and must not move the
            // high-water mark, otherwise it launders the jump into
            // "already watched" before onSeeking gets a chance to block it.
            const delta = currentTime - lastTimeUpdateRef.current;
            if (delta >= 0 && delta <= 2) {
              maxWatchedTimeRef.current = Math.max(
                maxWatchedTimeRef.current,
                currentTime,
              );
            }
          }
          lastTimeUpdateRef.current = currentTime;
          if (currentTime - lastReportedRef.current >= 5) {
            lastReportedRef.current = currentTime;
            onProgress?.(currentTime, playerRef.current?.duration ?? 0);
          }
        }}
        onPause={() => {
          // Report the current time of the report immediately on pause so
          // resuming later picks up from exactly where playback stopped.
          if (playerRef.current) {
            const currentTime = playerRef.current.currentTime;
            lastReportedRef.current = currentTime;
            onProgress?.(currentTime, playerRef.current.duration ?? 0);
          }
        }}
        onSeeking={(targetTime: number) => {
          if (
            isMandatory &&
            !unlockedRef.current &&
            targetTime > maxWatchedTimeRef.current + 0.5 &&
            playerRef.current
          ) {
            playerRef.current.currentTime = maxWatchedTimeRef.current;
          }
        }}
        onSeeked={() => {
          // Scrubbing the progress bar doesn't necessarily produce another
          // onTimeUpdate/onPause tick before the learner navigates away, so
          // report the seeked-to position immediately, same as onPause.
          if (playerRef.current) {
            const currentTime = playerRef.current.currentTime;
            lastReportedRef.current = currentTime;
            lastTimeUpdateRef.current = currentTime;
            onProgress?.(currentTime, playerRef.current.duration ?? 0);
          }
        }}
        onEnded={() => {
          unlockedRef.current = true;
          onReachedEnd?.();
        }}
        style={{
          height: "100%",
          width: "100%",
          backgroundColor: "#fff",
          border: "none",
          padding: "0px",
          // Vidstack's default layout rounds the player corners (6px); the
          // video design uses sharp edges.
          borderRadius: 0,
          "--video-border-radius": "0px",
        }}
      >
        <MediaProvider />
        <DefaultVideoLayout
          icons={defaultLayoutIcons}
          colorScheme="light"
          slots={{
            pipButton: null,
            airPlayButton: null,
            googleCastButton: null,
            bufferingIndicator: null,
          }}
        />
      </MediaPlayer>
      {buffering && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.2)",
            pointerEvents: "none",
          }}
        >
          <CircularProgress sx={{ color: "#fff" }} />
        </Box>
      )}
    </Box>
  );
};
